import React, { useEffect, useRef, useState } from 'react';
import { Button, CloseButton, Form, Image, Spinner } from 'react-bootstrap';
import { connect, useDispatch, useSelector } from 'react-redux';

import { workflowToChatMessages } from '../processWorkflowInput/workflowJsonProcessors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
  useGetSessionById,
  useUploadImageToS3_Mutation,
} from 'stateManagement/queries/services/aiChat/aiChatQueries';

import {
  selectActionPlanById,
  selectActionPlans,
} from 'stateManagement/redux/actionPlans/selector';
import { clearActionPlanLogs } from 'stateManagement/redux/actionPlans/slice';
import { ACTION_PLAN_STATUSES } from 'stateManagement/redux/actionPlans/types';

import WorkflowDropdown from '../workflows/components/WorkflowDropdown';

const ChatInput = ({
  sessionId,
  input,
  handleInputChange,
  setInput,
  handleSubmit,
  append,
  stop,
  isStreaming,
  setMessages,
  messages,
  setIsActionPlanMessage,
  authReducer: { user },
  isDisabled = false,
}) => {
  const dispatch = useDispatch();
  const { data: chatData } = useGetSessionById(sessionId);
  const [imagesInput, setImagesInput] = useState([]);
  const [images, setImages] = useState([]);
  const [jsonFiles, setJsonFiles] = useState([]);
  const [selectedJsons, setSelectedJsons] = useState([]);
  const textareaRef = useRef(null);

  // Get all action plans from Redux
  const actionPlans = useSelector(selectActionPlans);

  // Get the current action plan for this session
  const currentPlan = useSelector((state) => {
    const planForSession = actionPlans.find(
      (p) => p.chatSessionId === sessionId
    );
    return selectActionPlanById(state, planForSession?.id);
  });

  const resetTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '38px';
    }
  };

  const adjustHeight = () => {
    if (textareaRef.current) {
      const element = textareaRef.current;
      element.style.height = '38px';
      const newHeight = element.scrollHeight;
      element.style.height = `${newHeight}px`;
    }
  };

  useEffect(() => {
    resetTextareaHeight();
  }, []);

  useEffect(() => {
    if (input) {
      adjustHeight();
    }
  }, [input]);

  const {
    mutate: uploadImage,
    data: uploadedImages,
    isLoading,
  } = useUploadImageToS3_Mutation();

  const scrollToBottom = (smooth = true) => {
    const parentDiv = document.querySelector('#scrollable-panel-content');
    if (parentDiv) {
      parentDiv.style.overflow = 'auto';
      parentDiv.scrollTo({
        top: parentDiv.scrollHeight,
        behavior: smooth ? 'smooth' : 'auto',
      });
    }
  };

  useEffect(() => {
    if (isStreaming) {
      const scrollInterval = setInterval(scrollToBottom, 100);
      return () => clearInterval(scrollInterval);
    }
  }, [isStreaming]);

  // use effect runs after images have been uploaded
  useEffect(() => {
    if (uploadedImages?.length > 0) {
      // Set isActionPlanMessage to false for image uploads
      if (setIsActionPlanMessage) {
        setIsActionPlanMessage(false);
      }

      // Pass all data collected in workflow to be processed into messages to send to chat
      const { messages: processedMessages } = workflowToChatMessages(
        jsonFiles,
        currentPlan,
        input,
        uploadedImages
      );

      // If we have processed messages, use them
      if (processedMessages) {
        const { dataMessages, userMessage } = processedMessages;

        // Add data messages to chat if they exist
        if (dataMessages && dataMessages.length > 0) {
          if (Array.isArray(messages)) {
            // Add all data messages to the chat
            const updatedMessages = [...messages];
            dataMessages.forEach((msg) => updatedMessages.push(msg));
            setMessages(updatedMessages);
          } else {
            // Initialize if messages is not an array
            setMessages([...dataMessages]);
          }
        }

        // Append user message (this will trigger the API request)
        if (userMessage) {
          append(userMessage);
        }
      }

      let chatActionPlan;
      for (const plan of actionPlans) {
        const { createdAt, chatSessionId, status } = plan;
        if (
          new Date(createdAt) > new Date(chatActionPlan?.createdAt || 0) &&
          chatSessionId === sessionId &&
          status === ACTION_PLAN_STATUSES.COMPLETED
        ) {
          chatActionPlan = plan;
        }
      }
      if (chatActionPlan) {
        dispatch(clearActionPlanLogs({ id: chatActionPlan.id }));
      }
      setInput('');
      setJsonFiles([]);
      setSelectedJsons([]);
      resetTextareaHeight();
      setTimeout(scrollToBottom, 100);
    }
  }, [uploadedImages]);

  useEffect(() => {
    let chatActionPlan;
    for (const plan of actionPlans) {
      const { createdAt, chatSessionId, status } = plan;
      if (
        new Date(createdAt) > new Date(chatActionPlan?.createdAt || 0) &&
        chatSessionId === sessionId &&
        status === ACTION_PLAN_STATUSES.COMPLETED
      ) {
        chatActionPlan = plan;
      }
    }
    if (chatActionPlan) {
      const screenshots = chatActionPlan.logs.reduce((acc, log) => {
        if (log.screenshot) {
          acc.push(log.screenshot);
        }
        return acc;
      }, []);

      // Process JSON files from rawData in logs
      const jsonFiles = chatActionPlan.logs.reduce((acc, log) => {
        if (log.rawData) {
          try {
            acc.push({
              data: log.rawData,
              timestamp: log.timestamp,
            });
          } catch (e) {
            // Skip if there's an error processing the rawData
            console.error('Error processing rawData:', e);
          }
        }
        return acc;
      }, []);

      if (screenshots?.length > 0 || jsonFiles?.length > 0) {
        // Set isActionPlanMessage to true when displaying action plan content
        if (setIsActionPlanMessage) {
          setIsActionPlanMessage(true);
        }

        setImages(screenshots);
        setImagesInput(screenshots);
        setJsonFiles(jsonFiles);
        setInput(chatActionPlan.prompt);

        for (let i = 0; i < screenshots.length; i++) {
          fetch(screenshots[i])
            .then((res) => res.blob())
            .then((blob) => {
              setImagesInput((prev) =>
                prev.map((img, index) => (index === i ? blob : img))
              );
            });
        }
      } else {
        setImages([]);
        setImagesInput([]);
        setJsonFiles([]);
        setInput('');
        resetTextareaHeight();
      }
    } else {
      // Set isActionPlanMessage to false when no action plan is active
      if (setIsActionPlanMessage) {
        setIsActionPlanMessage(false);
      }

      setInput('');
      resetTextareaHeight();
    }
  }, [actionPlans, setIsActionPlanMessage]);

  const handlePaste = (e) => {
    const items = Array.from(e.clipboardData.items);
    const imageFiles = items
      .filter((item) => item.type.startsWith('image/'))
      .map((item) => item.getAsFile());

    if (imageFiles.length > 0) {
      e.preventDefault();
      handleImageFiles(imageFiles);
    }
  };

  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );
    handleImageFiles(files);
  };

  const handleImageFiles = (files) => {
    setImagesInput((prev) => [...prev, ...files]);
    files.forEach((file) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages((prev) => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() || imagesInput.length > 0) {
        const form = e.target.closest('form');
        form.dispatchEvent(
          new Event('submit', { cancelable: true, bubbles: true })
        );
      }
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    // Set isActionPlanMessage for regular submissions based on current state
    if (setIsActionPlanMessage) {
      const hasActionPlanContent = jsonFiles.length > 0 || images.length > 0;
      setIsActionPlanMessage(hasActionPlanContent);
    }

    if (imagesInput.length > 0) {
      uploadImage({
        sessionId,
        images: imagesInput,
      });

      setImagesInput([]);
      setImages([]);
      setSelectedJsons([]);
    } else {
      handleSubmit(e);

      setInput('');
      setSelectedJsons([]);
      resetTextareaHeight();
      setTimeout(scrollToBottom, 100);
    }
  };

  return chatData?.createdBy?._id === user._id ? (
    <div className='position-absolute bottom-0 w-100 pe-5 bg-white py-3 ps-2'>
      {(images.length > 0 || jsonFiles.length > 0) && (
        <div
          className='mb-2 bg-500 p-2 d-flex align-items-center'
          style={{ overflowX: 'scroll' }}
        >
          <CloseButton
            className='ms-2 me-4'
            onClick={() => {
              setImages([]);
              setImagesInput([]);
              setJsonFiles([]);
              setSelectedJsons([]);

              // Reset isActionPlanMessage when clearing content
              if (setIsActionPlanMessage) {
                setIsActionPlanMessage(false);
              }
            }}
          />
          {images.map((image, index) => (
            <Image
              className='me-2'
              src={image}
              key={`image-input-buffer-${index}`}
              height={50}
              width={50}
            />
          ))}
          {jsonFiles.map((json, index) => (
            <div
              key={`json-preview-${index}`}
              className='me-2 bg-white rounded cursor-pointer'
              style={{ height: 50, width: 50, padding: 4 }}
              onClick={() => {
                setSelectedJsons((prev) => {
                  const isSelected = prev.some(
                    (j) => j.timestamp === json.timestamp
                  );
                  if (isSelected) {
                    return prev.filter((j) => j.timestamp !== json.timestamp);
                  } else {
                    return [...prev, json];
                  }
                });
              }}
            >
              <div
                className={`h-100 w-100 d-flex align-items-center justify-content-center rounded ${
                  selectedJsons.some((j) => j.timestamp === json.timestamp)
                    ? 'bg-primary text-white'
                    : 'bg-light'
                }`}
              >
                <span className='fw-bold fs--1'>JSON</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedJsons.length > 0 && (
        <div className='mb-2 bg-light p-2 rounded'>
          <div className='d-flex justify-content-between align-items-start mb-2'>
            <div
              className='overflow-auto'
              style={{ maxHeight: '200px', width: '100%' }}
            >
              {selectedJsons.map((json, index) => (
                <pre key={json.timestamp} className='text-xs m-0 mb-2'>
                  {JSON.stringify(json.data, null, 2)}
                </pre>
              ))}
            </div>
            <Button
              variant='outline-danger'
              size='sm'
              className='ms-2 py-0 px-2'
              style={{ fontSize: '0.75rem', minWidth: '50px' }}
              onClick={() => setSelectedJsons([])}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {isLoading && (
        <div className='d-flex align-items-center mb-1'>
          <Spinner className='me-2' size='sm' />
          Analyzing data...
        </div>
      )}
      <div>
        <Form
          className='chat-editor-area pt-3'
          style={{ border: 'none' }}
          onSubmit={handleFormSubmit}
        >
          <Form.Control
            as='textarea'
            ref={textareaRef}
            name='prompt'
            value={input}
            disabled={isDisabled}
            onChange={(e) => {
              handleInputChange(e);
              const element = e.target;
              element.style.height = '38px';
              const newHeight = element.scrollHeight;
              element.style.height = `${newHeight}px`;
            }}
            onKeyDown={handleKeyDown}
            onDrop={handleImageDrop}
            onPaste={handlePaste}
            onDragOver={(e) => e.preventDefault()}
            placeholder='Type your message'
            style={{
              resize: 'none',
              overflow: 'auto',
              padding: '8px 12px',
              lineHeight: '22px',
              minHeight: '38px',
              maxHeight: '200px',
            }}
            autoComplete='off'
          />
          <Form.Group controlId='chatFileUpload'>
            <Form.Label className='chat-file-upload cursor-pointer'>
              <FontAwesomeIcon icon='paperclip' />
            </Form.Label>
            <Form.Control
              type='file'
              className='d-none'
              accept='image/*'
              multiple
              onChange={(e) => handleImageFiles(Array.from(e.target.files))}
            />
          </Form.Group>
          <Button
            variant='falcon-primary'
            size='sm'
            className='me-1'
            type='submit'
            disabled={isDisabled}
          >
            Send
          </Button>
          <WorkflowDropdown isDisabled={isDisabled} />
          {isStreaming && (
            <FontAwesomeIcon
              icon='circle-stop'
              className='mx-2 text-danger cursor-pointer'
              onClick={stop}
              size='2x'
            />
          )}
        </Form>
      </div>
    </div>
  ) : null;
};

export default connect((state) => ({
  authReducer: state.authReducer,
}))(ChatInput);
